import './accounts.css';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, errorText } from '../../api';
import { formatIban, warnings } from '../../auth/company';
import { useConfig } from '../../lib/config';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import type { Ustva } from '../../types';
import { useKanzlei } from '../context';
import {
  FehltHinweis,
  LEER,
  PreisFelder,
  preiseBody,
  Vorschau,
  type Entwurf,
  type Preise,
  type PreiseBody,
  type VertragStand,
} from '../vertrag/Vertrag';
import { addressLine, consentText, fitsArea, staffLabel, USTVA_OPTS } from './labels';
import type { RegistrationDetail } from './types';

// „Registrierung prüfen“: Angaben des Mandanten mit Warnhinweisen, Freigabe oder Ablehnung. Nur Admin.
export function RegistrationReview() {
  const { id = '' } = useParams();
  const { me } = useKanzlei();
  const admin = me.user.isAdmin;
  // 'team': neu angemeldete Mitarbeiter erscheinen sofort in den Auswahlfeldern
  const d = useApi<RegistrationDetail>(admin ? `/api/kanzlei/registrations/${id}` : null, { live: ['clients', 'team'], clientId: id });

  if (!admin) return <Navigate to="/kanzlei/mandanten" replace />;
  if (d.error && !d.data) return <Navigate to="/kanzlei/mandanten" replace />;
  if (!d.data) return <div className="spinner" aria-label="Lädt" />;
  return <Review data={d.data} reload={d.reload} />;
}

function Row({ k, v, warn }: { k: string; v: ReactNode; warn?: string | null }) {
  const empty = v === null || v === undefined || v === '';
  return (
    <>
      <dt>{k}</dt>
      <dd>
        {empty ? <span className="note">fehlt</span> : v}
        {warn && (
          <>
            {' '}
            <span className="pill p-warn">⚠ {warn}</span>
          </>
        )}
      </dd>
    </>
  );
}

function Review({ data, reload }: { data: RegistrationDetail; reload(): Promise<void> }) {
  const g = data.registration;
  const navigate = useNavigate();
  const toast = useToast();
  const config = useConfig();
  const [nr, setNr] = useState<string | null>(null);
  const [ustva, setUstva] = useState<Ustva>('Q');
  const [fibu, setFibu] = useState('');
  const [lohn, setLohn] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  // Mandatsvertrag: gebuchte Module und Preise; fehlen Angaben des Büros, gibt es keinen Vertrag
  const vs = useApi<VertragStand>(g.status === 'pending' ? `/api/kanzlei/clients/${g.id}/vertrag` : null, { live: ['abrechnung'] });
  const [preise, setPreise] = useState<Preise>(LEER);
  const [vorschau, setVorschau] = useState<(PreiseBody & Entwurf) | null>(null);
  const nrRef = useRef<HTMLInputElement>(null);
  const number = nr ?? data.nextNumber ?? '';
  const w = warnings(g);

  useEffect(() => {
    document.title = `Registrierung prüfen · ${g.name}`;
  }, [g.name]);

  const statusWord = g.status === 'pending' ? 'wartet auf Freigabe' : g.status === 'active' ? 'freigegeben' : 'abgelehnt';

  async function approve(e: FormEvent) {
    e.preventDefault();
    // Während die Ablehnung offen ist, gibt Enter (z. B. in „Mandantennummer“) nicht frei
    if (rejecting) return;
    if (!/^\d{5}$/.test(number)) {
      toast('Bitte eine 5-stellige Mandantennummer eingeben');
      nrRef.current?.focus();
      return;
    }
    const pv = !vs.data || vs.data.fehlt ? { body: null } : preiseBody(preise);
    if ('error' in pv) {
      toast(pv.error);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post<{
        mailSent: boolean;
        email: string | null;
        vertrag: { ok: true } | { ok: false; message: string } | null;
      }>(`/api/kanzlei/registrations/${g.id}/approve`, {
        number,
        ustva,
        fibuUserId: fibu || null,
        lohnUserId: lohn || null,
        vertrag: pv.body,
      });
      const done = res.mailSent
        ? `${g.name} freigegeben · Zugang freigeschaltet, E-Mail an ${res.email ?? g.loginEmail}`
        : `${g.name} freigegeben · Zugang freigeschaltet, ${g.contactName} kann sich sofort anmelden`;
      toast(
        !res.vertrag
          ? done
          : res.vertrag.ok
            ? `${done} · Vertrag liegt zum Unterschreiben in der App`
            : `${done} · Vertrag nicht erstellt: ${res.vertrag.message} Bitte in den Stammdaten neu erstellen.`,
      );
      navigate(`/kanzlei/mandanten/${g.id}/stammdaten`);
      window.scrollTo(0, 0);
    } catch (err) {
      toast(errorText(err));
      if (err instanceof ApiError && (err.code === 'number_taken' || err.code === 'bad_number')) nrRef.current?.focus();
      else void reload();
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/registrations/${g.id}/reject`, { reason: why.trim() });
      toast(`Registrierung von ${g.name} abgelehnt · wieder öffnen unter Mandanten › Abgelehnte`);
      navigate('/kanzlei/mandanten');
    } catch (err) {
      toast(errorText(err));
      setBusy(false);
      void reload();
    }
  }

  async function reopen() {
    setBusy(true);
    try {
      await api.post(`/api/kanzlei/registrations/${g.id}/reopen`);
      toast(`${g.name} wartet wieder auf Freigabe`);
      setRejecting(false);
      await reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const staffSelect = (sid: string, area: 'fibu' | 'lohn', label: string, value: string, set: (v: string) => void) => (
    <label htmlFor={sid}>
      {label}
      <select id={sid} value={value} onChange={(e) => set(e.target.value)}>
        <option value="">später zuweisen</option>
        {data.staff
          .filter((s) => fitsArea(s, area))
          .map((s) => (
            <option key={s.id} value={s.id}>
              {staffLabel(s)}
            </option>
          ))}
      </select>
    </label>
  );

  return (
    <div className="acc">
      <div className="crumb">
        <Link to="/kanzlei/mandanten">Mandanten</Link> › Registrierung prüfen
      </div>
      <div className="head">
        <div>
          <h1>{g.name}</h1>
          <p>
            Hat sich am {fmtDate(g.registeredAt)} in der App registriert · {statusWord}
          </p>
        </div>
      </div>
      <div className="grid2">
        <section className="card" aria-labelledby="rv-data">
          <div className="ch">
            <h3 id="rv-data">Angaben des Mandanten</h3>
            <span>so eingegeben</span>
          </div>
          <dl className="dl">
            <Row k="Firma" v={g.name} />
            <Row k="Rechtsform" v={g.legalForm} warn={w.legalForm} />
            <Row k="Land" v={g.country === 'AE' ? 'Dubai (VAE)' : 'Deutschland'} />
            <Row k="Adresse" v={addressLine(g)} warn={w.zip} />
            <Row k="Ansprechpartner" v={g.contactName} />
            <Row k="E-Mail (Login)" v={g.loginEmail} />
            <Row k="Telefon" v={g.contactPhone} />
            <Row k="Steuernummer" v={g.taxNumber} />
            <Row k={g.country === 'AE' ? 'TRN' : 'USt-ID'} v={g.vatId} />
            <Row k="Mitarbeiter" v={g.employees ?? null} />
            <Row k="Verträge" v={consentText(g.consent)} />
          </dl>
          <div className="ch" style={{ marginTop: 16 }}>
            <h3>Bankverbindung</h3>
          </div>
          <dl className="dl">
            <Row k="Kontoinhaber" v={g.accountHolder} />
            <Row k="IBAN" v={g.iban ? formatIban(g.iban) : null} warn={w.iban} />
            <Row k="BIC" v={g.bic} />
            <Row k="Bank" v={g.bankName} />
          </dl>
        </section>
        <section className="card" aria-labelledby="rv-ok">
          <div className="ch">
            <h3 id="rv-ok">Freigabe</h3>
          </div>
          {g.status === 'active' ? (
            <p>
              <span className="pill p-ok">freigegeben</span>
            </p>
          ) : g.status === 'rejected' ? (
            <>
              <p>
                <span className="pill p-bad">abgelehnt</span>
                {g.rejectReason && <span className="note"> Grund: {g.rejectReason}</span>}
              </p>
              <p>
                <button type="button" className="btn sm" onClick={() => void reopen()} disabled={busy}>
                  Wieder öffnen
                </button>
              </p>
            </>
          ) : (
            <>
              <form className="form" onSubmit={approve} noValidate>
                <label htmlFor="r-nr">
                  Mandantennummer *
                  <input
                    id="r-nr"
                    ref={nrRef}
                    required
                    value={number}
                    onChange={(e) => setNr(e.target.value)}
                    inputMode="numeric"
                    pattern="\d{5}"
                    maxLength={5}
                    title="5-stellige Zahl"
                    autoComplete="off"
                  />
                </label>
                {g.country === 'DE' && (
                  <label htmlFor="r-r">
                    UStVA-Einstufung
                    <select id="r-r" value={ustva} onChange={(e) => setUstva(e.target.value as Ustva)}>
                      {USTVA_OPTS.map(([k, label]) => (
                        <option key={k} value={k}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {staffSelect('r-fibu', 'fibu', 'Zuständig für Finanzbuchhaltung', fibu, setFibu)}
                {staffSelect('r-lohn', 'lohn', 'Zuständig für Lohn & Gehalt', lohn, setLohn)}
                {vs.data && (
                  <div className="vt-freigabe" role="group" aria-labelledby="r-vt-h">
                    <h4 id="r-vt-h">Mandatsvertrag</h4>
                    {vs.data.fehlt ? (
                      <FehltHinweis text={`${vs.data.fehlt} Ohne diese Angaben wird kein Vertrag erstellt.`} />
                    ) : (
                      <>
                        <PreisFelder idPrefix="r-vt" value={preise} onChange={setPreise} />
                        <p className="note">
                          {preise.fibu || preise.lohn
                            ? `Die App füllt damit den Vertrag aus und legt ihn ${g.contactName} zum Unterschreiben in die App${preise.lohn ? ', dazu die Vollmachten für Finanzamt und Sozialversicherung' : ''}.`
                            : 'Ohne Haken wird kein Vertrag erstellt.'}
                        </p>
                        {(preise.fibu || preise.lohn) && (
                          <div>
                            <button
                              type="button"
                              className="btn sm"
                              onClick={() => {
                                const r = preiseBody(preise);
                                if ('error' in r) toast(r.error);
                                else if (r.body) setVorschau({ ...r.body, number, ustva, fibuUserId: fibu || null, lohnUserId: lohn || null });
                              }}
                            >
                              Vorschau des Vertrags
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                <p className="note">
                  {config.mailEnabled
                    ? `Nach der Freigabe bekommt ${g.contactName} eine E-Mail und kann sich sofort anmelden.`
                    : `Nach der Freigabe kann sich ${g.contactName} sofort anmelden.`}
                </p>
                {!rejecting && (
                  <div className="btnrow">
                    <button type="submit" className="btn dark" disabled={busy}>
                      Freigeben und Zugang freischalten
                    </button>
                    <button type="button" className="btn" onClick={() => setRejecting(true)}>
                      Ablehnen
                    </button>
                  </div>
                )}
              </form>
              {rejecting && (
                // Rückfrage außerhalb des Formulars: Enter im Grund-Feld gibt nicht frei
                <div className="confirmup warn form" style={{ marginTop: 12 }} role="group" aria-labelledby="rv-no">
                  <b id="rv-no">Registrierung von {g.name} wirklich ablehnen?</b>
                  <label htmlFor="r-why">
                    Grund (optional, sieht {g.contactName})
                    <input
                      id="r-why"
                      maxLength={200}
                      placeholder="z. B. Bitte rufen Sie uns kurz an"
                      value={why}
                      onChange={(e) => setWhy(e.target.value)}
                      autoFocus
                    />
                  </label>
                  <div className="btnrow">
                    <button type="button" className="btn sm dark" onClick={() => void reject()} disabled={busy}>
                      Ja, ablehnen
                    </button>
                    <button type="button" className="btn sm" onClick={() => setRejecting(false)}>
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
      {vorschau && <Vorschau clientId={g.id} body={vorschau} onClose={() => setVorschau(null)} />}
    </div>
  );
}
